import { router } from 'expo-router';
import * as React from 'react';
import Animated, { FadeIn } from 'react-native-reanimated';
import {
    FullScreenSpinner,
    InfoButton,
    isWeb,
    SafeAreaView,
    View,
    XStack,
} from 'smoothie';

import TabHeader from '../../common/components/TabHeader';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { useTrackViewOnFocus } from '../../events/hooks/useTrackViewOnFocus';
import { captureException } from '../../logger/Logger';
import { useMoneyMapWorkflow } from '../../roadmap/hooks/useMoneyMapWorkflow';
import useGetIncomeSplitFromGlobalState from '../hooks/income-split/useGetIncomeSplitFromGlobalState';
import useGetExistingPaycheckConfigs from '../hooks/paycheck-routing/useGetPaycheckConfigs';
import useGetAllPaychecks from '../hooks/paycheck-routing/useGetPaychecks';
import useGetAllBankAccountDetails from '../hooks/useGetAllBankAccountDetails';
import useGetCurrentInterestRateAndApy from '../hooks/useGetCurrentInterestRateAndApy';
import CashAccountSplash from './cash-account-splash/CashAccountSplash';
import SpacesOverview from './spaces/SpacesOverview';

const FAQHeader = () => {
    return (
        <SafeAreaView
            noBottomPadding={true}
            edges={['top', 'left', 'right']}
            style={{ width: '100%' }}
        >
            <XStack
                flex={1}
                alignItems="center"
                justifyContent="flex-end"
                paddingTop={8}
                paddingHorizontal={8}
                width="100%"
                maxWidth={600}
                alignSelf="center"
            >
                <InfoButton
                    onPress={() => {
                        router.navigate('/settings/faq');
                    }}
                />
            </XStack>
        </SafeAreaView>
    );
};

const BankingOverview = ({
    includeHeader = true,
}: {
    includeHeader?: boolean;
}) => {
    useTrackViewOnFocus('BankingOverview');

    const user = useAuthedUser();

    const {
        isLoading: isLoadingBankAccountDetails,
        errorFetching,
        hasAccount,
        totalCurrentBalance,
        totalAvailableBalance,
        totalCardBalance,
        totalPendingTransfers,
        totalPendingCardTransactions,
        totalInterestEarned,
        totalCashBackEarned,
        bankAccountDetails,
    } = useGetAllBankAccountDetails();

    const { data: currentInterestRate, isLoading: isLoadingInterestRate } =
        useGetCurrentInterestRateAndApy({});

    const { isLoading: isLoadingPaychecks } = useGetAllPaychecks();

    const { isLoading: isLoadingPaycheckConfigs } =
        useGetExistingPaycheckConfigs();

    const { isLoading: isLoadingIncomeSplit } =
        useGetIncomeSplitFromGlobalState();

    const {
        moneyMap,
        isWorkflowCompleted: isMoneyMapCompleted,
        isLoadingMoneyMap,
        isLoadingCompleteness: isLoadingMoneyMapCompleteness,
    } = useMoneyMapWorkflow();

    if (
        !hasAccount &&
        (isLoadingBankAccountDetails ||
            isLoadingInterestRate ||
            isLoadingIncomeSplit ||
            isLoadingMoneyMap ||
            isLoadingMoneyMapCompleteness ||
            isLoadingPaycheckConfigs ||
            isLoadingPaychecks)
    ) {
        return <FullScreenSpinner />;
    }

    if (errorFetching) {
        // TODO: Render error state
        captureException(new Error('Error fetching banking details for user'));
    }

    return (
        <View flex={1} testID="banking-tab">
            {includeHeader &&
                (hasAccount ? (
                    <TabHeader headerTitle="Fruitful Cash" />
                ) : (
                    <FAQHeader />
                ))}
            <View flex={1}>
                <Animated.View
                    entering={FadeIn.duration(500)}
                    style={{ flex: 1, ...(isWeb && { overflowY: 'auto' }) }}
                >
                    {hasAccount ? (
                        <SpacesOverview
                            totalCurrentBalance={totalCurrentBalance}
                            totalAvailableBalance={totalAvailableBalance}
                            totalCardBalance={totalCardBalance}
                            totalPendingTransfers={totalPendingTransfers}
                            totalPendingCardTransactions={
                                totalPendingCardTransactions
                            }
                            totalInterestEarned={totalInterestEarned}
                            totalCashbackEarned={totalCashBackEarned}
                            bankAccountDetails={bankAccountDetails}
                            isMoneyMapCompleted={isMoneyMapCompleted}
                            moneyMap={moneyMap}
                        />
                    ) : (
                        <CashAccountSplash
                            currentInterestRate={currentInterestRate}
                            user={user}
                        />
                    )}
                </Animated.View>
            </View>
        </View>
    );
};

export default BankingOverview;
